import type {ReactNode} from 'react';
import {flexRender, type Row, type Table} from '@tanstack/react-table';
import {classNames} from 'utils';

interface ITableCommonProps<T> {
  table: Table<T>;
  filters?: ReactNode;
  onRowClick?: (row: Row<T>) => void;
}

const TableCommon = <T,>({table, filters, onRowClick}: ITableCommonProps<T>) => {
  return (
    <div className="mt-8 mx-4 sm:mx-0 flow-root">
      {/* Filters */}
      {!!filters && (
        <div className="-mx-4 -my-2 sm:mx-0 sm:my-0 p-3 border border-slate-50 dark:border-slate-500 border-b-gray-300 dark:border-b-white shadow-sm ring-1 ring-black/5 sm:rounded-t-lg">
          {filters}
        </div>
      )}
      {/* Table */}
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div
            className={classNames(
              'overflow-hidden border border-slate-50 dark:border-slate-500 shadow-sm ring-1 ring-black/5 ',
              filters ? 'sm:rounded-b-lg' : 'sm:rounded-lg',
            )}>
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50 dark:bg-slate-900">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        scope="col"
                        className="h-14 py-3.5 pr-3 pl-4 text-left text-sm font-semibold whitespace-nowrap text-gray-900 first:sm:pl-6 dark:text-white">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    className={classNames('bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700', !!onRowClick && 'cursor-pointer')}
                    onClick={() => onRowClick?.(row)}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 dark:text-white first:sm:pl-6">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableCommon;
